import { z } from "zod";

import { fetchWithError, useParsedQueries, useParsedQuery } from "@nens/react-query-utils";
import { combineUrlAndParams } from "@nens/shared-utils";

import { lizardDateSchema } from "../dates";
import { paginatedSchema } from "../pagination";

import { organisationSchema } from "./organisations";

export const projectListItemSchema = z.object({
  uuid: z.string(),
  url: z.string(),
  name: z.string(),
  code: z.string(),
  description: z.string().nullable(),
  organisation: organisationSchema,
  supplier: z.string().nullable(),
  access_modifier: z.string(),
  scheduled_for_deletion: z.boolean(),
  last_modified: lizardDateSchema.nullable(),
  created: lizardDateSchema,
});

export type ProjectListItem = z.infer<typeof projectListItemSchema>;

export const projectListSchema = paginatedSchema(projectListItemSchema);
export type ProjectList = z.infer<typeof projectListSchema>;

export const projectSchema = projectListItemSchema;
export type Project = ProjectListItem;

export function useProjects(params = {}) {
  const url = combineUrlAndParams("/api/v4/projects/", params);
  return useParsedQuery<ProjectList>(
    ["lizard-api-v4", "projects", url],
    () => fetchWithError(url),
    (json: unknown) => projectListSchema.parse(json)
  );
}

export function useAllProjects(params = {}) {
  const url = combineUrlAndParams("/api/v4/projects/?page_size=0", params);
  return useParsedQuery<Project[]>(
    ["lizard-api-v4", "projects", url],
    () => fetchWithError(url),
    (json: unknown) => z.array(projectSchema).parse(json)
  );
}

export function useProject(uuid: string) {
  return useParsedQuery<Project>(
    ["lizard-api-v4", "projects", uuid],
    () => fetchWithError(`/api/v4/projects/${uuid}/`),
    (json: unknown) => projectSchema.parse(json)
  );
}

export function useMultipleProjects(uuids: string[]) {
  return useParsedQueries(
    uuids.map((uuid) => {
      return {
        queryKey: ["lizard-api-v4", "projects", uuid],
        fetchFunction: () => fetchWithError(`/api/v4/projects/${uuid}/`),
        parseFunction: (json: unknown) => projectSchema.parse(json),
      };
    })
  );
}
